// F-148 §10.3 discrepancy CSV (AC22) with export hardening (SEC-N7). Built client-side from the
// fetched receipt: UTF-8 BOM, CRLF, every text cell quoted, no mutation.

import type { StockReceipt, StockReceiptLine } from '../types/stock-receiving';
import {
  RECEIVING_WHITESPACE,
  TEXT_BIDI_CONTROLS,
  UTF8_BOM,
  codePointClass,
} from './receiving-normalize';

export const DISCREPANCY_CSV_COLUMNS = [
  'lineNo',
  'product',
  'variant',
  'barcode',
  'supplierCode',
  'supplierItemName',
  'expected',
  'accepted',
  'damaged',
  'missing',
  'corrected',
  'netAccepted',
  'note',
] as const;

export type DiscrepancyCsvColumn = (typeof DISCREPANCY_CSV_COLUMNS)[number];
/** Translated header labels, supplied by the caller via `t('admin.receiving.export.*')`. */
export type DiscrepancyCsvHeaders = Record<DiscrepancyCsvColumn, string>;

/** Integer columns generated by Dukanti: written unquoted and exempt from formula escaping. */
export const DISCREPANCY_CSV_NUMERIC_COLUMNS: ReadonlySet<DiscrepancyCsvColumn> = new Set([
  'lineNo',
  'expected',
  'accepted',
  'damaged',
  'missing',
  'corrected',
  'netAccepted',
]);

const BIDI_CONTROL_RE = new RegExp(codePointClass(TEXT_BIDI_CONTROLS), 'gu');
/** Leading run of U+061C, U+200B–200F, U+FEFF and Unicode whitespace (§10.3 step 3). */
const LEADING_INVISIBLE_OR_SPACE_RE = new RegExp(
  `^${codePointClass([0x061c, [0x200b, 0x200f], 0xfeff, ...RECEIVING_WHITESPACE])}+`,
  'u',
);
/** `= + - @ | %`, TAB, CR or LF as the first character (§10.3 step 4). */
const FORMULA_TRIGGERS: ReadonlySet<string> = new Set([
  '=',
  '+',
  '-',
  '@',
  '|',
  '%',
  String.fromCodePoint(0x0009),
  String.fromCodePoint(0x000d),
  String.fromCodePoint(0x000a),
]);

/**
 * §10.3: NFKC → remove bidi embedding/override/isolate controls anywhere → strip a leading run of
 * U+061C, U+200B–200F, U+FEFF and whitespace → prefix `'` when the first char is one of
 * `= + - @ | %`, TAB, CR or LF → double internal quotes. Returns the cell content without the
 * surrounding quotes (the writer adds them).
 */
export function escapeSpreadsheetCell(value: string): string {
  let cell = value.normalize('NFKC').replace(BIDI_CONTROL_RE, '');
  cell = cell.replace(LEADING_INVISIBLE_OR_SPACE_RE, '');
  if (cell.length > 0 && FORMULA_TRIGGERS.has(cell.charAt(0))) cell = `'${cell}`;
  return cell.replace(/"/gu, '""');
}

function quoted(value: string): string {
  return `"${escapeSpreadsheetCell(value)}"`;
}

function netAccepted(line: StockReceiptLine): number {
  return line.acceptedUnits - line.correctedUnits;
}

/** Lines with any discrepancy: damaged, missing, or a posted correction. */
export function selectDiscrepancyLines(receipt: StockReceipt): StockReceiptLine[] {
  return receipt.lines.filter(
    (line) => line.damagedUnits > 0 || line.missingUnits > 0 || line.correctedUnits > 0,
  );
}

function cellValue(line: StockReceiptLine, column: DiscrepancyCsvColumn): string {
  switch (column) {
    case 'lineNo':
      return String(line.lineNo);
    case 'product':
      return quoted(line.productNameSnapshot);
    case 'variant':
      return quoted(line.optionSummarySnapshot);
    case 'barcode':
      return quoted(line.barcodeSnapshot ?? line.identifiers.barcode ?? '');
    case 'supplierCode':
      return quoted(line.identifiers.supplierCode ?? '');
    case 'supplierItemName':
      return quoted(line.identifiers.supplierItemName ?? '');
    case 'expected':
      return String(line.expectedUnits);
    case 'accepted':
      return String(line.acceptedUnits);
    case 'damaged':
      return String(line.damagedUnits);
    case 'missing':
      return String(line.missingUnits);
    case 'corrected':
      return String(line.correctedUnits);
    case 'netAccepted':
      return String(netAccepted(line));
    case 'note':
      return quoted(line.note ?? '');
  }
}

/**
 * Builds the discrepancy CSV text. Quantity columns are integers generated by Dukanti and are
 * written unquoted; every text column goes through `escapeSpreadsheetCell` and is quoted.
 */
export function buildDiscrepancyCsv(receipt: StockReceipt, headers: DiscrepancyCsvHeaders): string {
  const headerRow = DISCREPANCY_CSV_COLUMNS.map((column) => quoted(headers[column])).join(',');
  const rows = selectDiscrepancyLines(receipt).map((line) =>
    DISCREPANCY_CSV_COLUMNS.map((column) => cellValue(line, column)).join(','),
  );
  return `${UTF8_BOM}${[headerRow, ...rows].join('\r\n')}\r\n`;
}

/** `dukanti-receipt-<first8>-discrepancies.csv` */
export function discrepancyCsvFileName(receiptId: string): string {
  return `dukanti-receipt-${receiptId.slice(0, 8)}-discrepancies.csv`;
}
